import {
    UsersIcon,
    BanknotesIcon,
    ChartBarIcon,
    TrophyIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    MinusIcon
} from "@heroicons/react/24/outline";
import LeaderboardTable from "./LeaderboardTable";
import MobileLeaderboardCard from "./MobileLeaderboardCard";
import useModal from "../../hooks/modalHook";
import SetAgentTarget from "../users/SetAgentTarget";
import { AgentTarget } from "../../resources/types";
import Button from "../../components/form-control/Button";
import { useQuery } from "@tanstack/react-query";
import { AGENT_ACHIEVEMENT_STATUS, AGENT_TARGET_PERIOD, RANK_AGENTS_BY } from "../../resources/enums";
import { useEffect, useState } from "react";
import moment from "moment";
import { upperCase } from "lodash";
import { getAgentsLeaderboard } from "../../api/requests";
import { CEDI_SIGN } from "../../resources/constants";
import { numberWithCommas } from "../../utilities/miscFunctions";
import SelectDropdown, { ISelectDropdownItem } from "../../components/form-control/SelectDropdown";
import useBranches from "../../hooks/BranchesHook";
import useUserAccount from "../../hooks/stateHooks/userAccountHook";

interface IProps {
    branchId?: string;
}

const INIT_DATA = {
    summary: {
        total_actual: 0,
        total_agents: 0,
        actual_percentage: 0,
        top_agent: {
            first_name: "",
            last_name: "",
            name: "",
            achievement: 0
        }
    },
    agents: []
}

const rankingOptions: ISelectDropdownItem[] = [
    {
        name: "Deposits Amount",
        value: RANK_AGENTS_BY.DEPOSIT_AMOUNT,
        id: RANK_AGENTS_BY.DEPOSIT_AMOUNT
    },
    {
        name: "Deposits Count",
        value: RANK_AGENTS_BY.DEPOSIT_COUNT,
        id: RANK_AGENTS_BY.DEPOSIT_COUNT
    },
    {
        name: "Customers Count",
        value: RANK_AGENTS_BY.CUSTOMER_COUNT,
        id: RANK_AGENTS_BY.CUSTOMER_COUNT
    }
]

const leaderboardPeriodOptions: ISelectDropdownItem[] = [
    {
        name: "This Month",
        value: moment(new Date()).format("YYYY-MM"),
        id: moment(new Date()).format("YYYY-MM")
    },
    {
        name: "Last Month",
        value: moment(new Date()).subtract(1, "months").format("YYYY-MM"),
        id: moment(new Date()).subtract(1, "months").format("YYYY-MM"),
    }
]

export default function AgentsLeaderboard(props: IProps) {
    const { updateModalStates, closeModal, ModalComponent } = useModal();
    const [rankBy, setRankBy] = useState<RANK_AGENTS_BY>(RANK_AGENTS_BY.DEPOSIT_AMOUNT);
    const [period, setPeriod] = useState<string>(moment(new Date()).format("YYYY-MM"));
    const [branchId, setBranchId] = useState<string>(props.branchId ?? "all");
    const { BRANCHES_LIST_FOR_FORM, fetchBranches } = useBranches();
    const { user } = useUserAccount();

    const { data: leaderboardData = INIT_DATA, refetch } = useQuery({
        queryKey: ([
            "leaderboard",
            rankBy,
            period,
            branchId
        ]),
        queryFn: () => getAgentsLeaderboard(
            rankBy,
            period,
            branchId === "all" ? "" : branchId
        ),
        staleTime: 1000 * 60 * 5,
    });

    useEffect(() => {
        fetchBranches();
    }, [])

    const setAgentTargets = () => {
        updateModalStates({
            show: true,
            children: <SetAgentTarget
                onComplete={(agentTargets: AgentTarget[]) => {
                    refetch();
                    closeModal();
                }}
                branchId={props.branchId}
            />,
            title: "Set Target"
        })
    }

    const summaryCards = [
        {
            icon: <ChartBarIcon className="w-6 h-6" />,
            title: "Overall",
            value: `${leaderboardData.summary.actual_percentage}%`,
            subtitle: "Across all agents"
        },
        {
            icon: < TrophyIcon className="w-6 h-6 text-yellow-500" />,
            title: "Top Agent",
            value: leaderboardData.summary.top_agent?.last_name ?? "",
            subtitle: `${leaderboardData.summary.top_agent?.achievement}% achieved`
        },
        {
            icon: <BanknotesIcon className="w-6 h-6 text-green-600" />,
            title: "Total Actual",
            value: `${CEDI_SIGN} ${numberWithCommas(leaderboardData.summary.total_actual)}`,
            subtitle: "Across all agents"
        },
        {
            icon: <UsersIcon className="w-6 h-6 text-blue-600" />,
            title: "Agents",
            value: leaderboardData.summary.total_agents,
            subtitle: `${leaderboardData.summary.total_agents} active`
        }
    ]

    return (
        <div className="bg-white dark:bg-primary-dark-surface rounded-2xl shadow-sm border border-gray-200 dark:border-primary-dark-border_active">

            {/* Header */}
            <div className="p-6 border-b dark:border-primary-dark-border_active">

                <h2 className="text-md md:text-2xl font-bold flex gap-x-4 items-center">
                    Agents Leaderboard
                    {user.isOwner() && (
                        <Button
                            label="Set Target"
                            onClick={setAgentTargets}
                            className="rounded bg-primary-secondary text-white text-xs md:text-md"
                        />
                    )}
                </h2>

                <p className="text-gray-500 dark:text-text-dark-secondary mt-1 text-xs md:text-md">
                    Monthly performance against assigned targets
                </p>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                    <SelectDropdown
                        list={leaderboardPeriodOptions}
                        listSelectedValue={period}
                        onChange={(value: string) => {
                            setPeriod(value);
                        }}
                        wrapperClasses="w-full rounded"
                        placeholder="Select period"
                    />
                    <SelectDropdown
                        list={rankingOptions}
                        listSelectedValue={rankBy}
                        onChange={(value: string) => {
                            setRankBy(value as RANK_AGENTS_BY);
                        }}
                        wrapperClasses="w-full rounded"
                        placeholder="Select ranking criteria"
                    />

                    {!props.branchId && (
                        <SelectDropdown
                            list={[{ name: "All Branches", value: "all", id: "all" }, ...BRANCHES_LIST_FOR_FORM]}
                            listSelectedValue={branchId}
                            onChange={(value: string) => {
                                setBranchId(value);
                            }}
                            wrapperClasses="w-full rounded"
                            placeholder="Select branch"
                        />
                    )}
                </div>
            </div>

            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 p-6">
                {summaryCards.map((summary, index: number) => (
                    <SummaryCard
                        key={index}
                        icon={summary.icon}
                        title={summary.title}
                        value={summary.value}
                        subtitle={summary.subtitle}
                    />
                ))}
            </div>

            {/* Desktop */}
            <div className="hidden lg:block">
                <LeaderboardTable agents={leaderboardData.agents} />
            </div>

            {/* Mobile */}
            <div className="lg:hidden space-y-4 p-5 overflow-y-auto max-h-96">
                {leaderboardData.agents.map((agent, index: number) => (
                    <MobileLeaderboardCard
                        key={index}
                        agent={agent}
                    />
                ))}

            </div>
            <ModalComponent />
        </div>

    );

}

function SummaryCard({
    icon,
    title,
    value,
    subtitle
}: any) {

    return (

        <div className="border rounded-xl p-5 dark:border-primary-dark-border_active">

            <div className="flex justify-between">

                <div>
                    <p className="text-sm text-gray-500 dark:text-text-dark-primary">
                        {title}
                    </p>

                    <h3 className="text-xl font-bold mt-2">
                        {value}
                    </h3>

                    <p className="text-xs text-gray-500 dark:text-text-dark-secondary mt-1">
                        {subtitle}
                    </p>
                </div>

                {icon}

            </div>

        </div>

    );

}

export interface Agent {
    id: number;
    rank: number;
    movement: "up" | "down" | "same";
    movementCount: number;
    initials: string;
    name: string;
    branch: string;
    target: number;
    actual: number;
    achievement: number;
    variance: number;
    status: AGENT_ACHIEVEMENT_STATUS;
    avatar: string;
}